import React, { useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import { safeAccessibilityRole } from '@/constants/utils/a11y';

const isWeb = Platform.OS === 'web';

const FACTS = [
  { icon: 'bullseye', titleKey: 'pages.agents.intro.fact1Title', bodyKey: 'pages.agents.intro.fact1Body' },
  { icon: 'comments', titleKey: 'pages.agents.intro.fact2Title', bodyKey: 'pages.agents.intro.fact2Body' },
  { icon: 'wand-magic-sparkles', titleKey: 'pages.agents.intro.fact3Title', bodyKey: 'pages.agents.intro.fact3Body' },
];

function AgentsIntroHero({ onCreate, onDiscoveryY, personalContent, search, children }) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM } = useResponsiveness();
  const compact = isXS || isSM;
  const discoveryY = useRef(0);
  const [showDetails, setShowDetails] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const palette = getCatalogPalette(theme);
  const styles = useMemo(() => createStyles(colors, compact, palette), [colors, compact, palette]);

  return (
    <View style={styles.wrap}>
      <View style={styles.hero} accessibilityRole={safeAccessibilityRole('banner')}>
        <View style={styles.heroGrid}>
          <View style={styles.intro}>
            <View style={styles.titleRow}>
              <Text tag="h1" weight="bold" style={styles.headline}>{t('navigation.agents')}</Text>
              <Pressable accessibilityRole="button" accessibilityLabel={t('pages.agents.aboutBots')}
                accessibilityState={{ expanded: showDetails }}
                {...(isWeb ? { 'aria-expanded': showDetails } : {})}
                onPress={() => setShowDetails(value => !value)}
                style={({ hovered, pressed }) => [styles.about, (hovered || pressed || showDetails) && { backgroundColor: palette.hover }]}>
                <Icon name="circle-info" size={13} color={colors.text_color_secondary} />
                <Text size="xs" color="secondary">{t('pages.agents.aboutBots')}</Text>
              </Pressable>
            </View>
            <Text tag="p" color="secondary" style={styles.lead}>{t('pages.agents.intro.lead')}</Text>
          </View>
          <View style={styles.heroActions}>
            <Button
              text={t('pages.agents.intro.createCta')}
              prependIcon="plus"
              variant="secondary"
              size="medium"
              textWeight="semibold"
              onPress={onCreate}
              style={{ backgroundColor: palette.ink, borderRadius: 10 }}
              textStyle={{ color: palette.onInk }}
              iconColor={palette.onInk}
              hoverBackgroundColor={palette.ink}
            />
            <Text size="xs" color="tertiary">{t('pages.agents.intro.createSubtitle')}</Text>
          </View>
        </View>

        {showDetails && <View style={styles.facts} accessibilityRole={safeAccessibilityRole('list')}>
          {FACTS.map((fact, index) => (
            <View key={fact.titleKey} style={[styles.fact, index === 0 && styles.factFirst]} accessibilityRole={safeAccessibilityRole('listitem')}>
              <View style={styles.factIcon}>
                <Icon name={fact.icon} size={15} color={palette.icon} />
              </View>
              <View style={styles.factCopy}>
                <Text size="s" weight="semibold">{t(fact.titleKey)}</Text>
                <Text size="xs" color="secondary" style={styles.factBody}>{t(fact.bodyKey)}</Text>
              </View>
            </View>
          ))}
        </View>}
      </View>

      {personalContent}

      <View style={styles.discover} onLayout={(event) => {
        const y = event.nativeEvent.layout.y;
        discoveryY.current = y;
        onDiscoveryY?.(y);
      }}>
        <View style={styles.discoverHead}>
          <Text tag="h2" size="l" weight="semibold">{t('pages.agents.intro.browseTitle')}</Text>
          <Text size="s" color="secondary">{t('pages.agents.intro.browseSubtitle')}</Text>
        </View>
        <View style={[styles.search, searchFocused && { borderColor: palette.activeBorder }]}>
          <Icon name="search" size={16} color={colors.text_color_secondary} />
          <TextInput
            value={search.value ?? ''}
            onChangeText={search.onChangeText}
            placeholder={search.placeholder}
            placeholderTextColor={colors.text_color_tertiary}
            style={styles.searchInput}
            autoCorrect={false}
            autoCapitalize="none"
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            accessibilityLabel={search.placeholder}
          />
          {!!search.value && (
            <Pressable
              onPress={() => search.onChangeText('')}
              accessibilityRole="button"
              accessibilityLabel={t('common.clear')}
              hitSlop={8}
              style={styles.clear}
            >
              <Icon name="xmark" size={14} color={colors.text_color_secondary} />
            </Pressable>
          )}
        </View>
        {children}
      </View>
    </View>
  );
}

AgentsIntroHero.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onDiscoveryY: PropTypes.func,
  personalContent: PropTypes.node,
  search: PropTypes.shape({
    value: PropTypes.string,
    onChangeText: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
  }).isRequired,
  children: PropTypes.node,
};

const createStyles = (colors, compact, palette) => StyleSheet.create({
  wrap: { width: '100%', paddingHorizontal: compact ? 16 : 24, paddingTop: compact ? 20 : 28 },
  hero: {
    marginBottom: 24,
  },
  heroGrid: {
    flexDirection: compact ? 'column' : 'row',
    alignItems: compact ? 'stretch' : 'center',
    gap: compact ? 16 : 32,
  },
  // The stacked layout must measure content height, not divide it with flex.
  intro: { flex: compact ? undefined : 1, minWidth: 0, gap: 8, maxWidth: 640 },
  titleRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 14 },
  about: { flexDirection: 'row', alignItems: 'center', gap: 6, padding: 8, borderRadius: 8 },
  headline: { fontSize: 32, lineHeight: 38, letterSpacing: -0.8 },
  lead: { fontSize: 14, lineHeight: 21, maxWidth: 600 },
  heroActions: {
    flexDirection: 'column',
    alignItems: compact ? 'flex-start' : 'center',
    gap: 8,
  },
  facts: {
    flexDirection: compact ? 'column' : 'row',
    minWidth: 0,
    borderWidth: 1,
    borderColor: palette.border,
    backgroundColor: palette.surface,
    borderRadius: 14,
    marginTop: 18,
    overflow: 'hidden',
  },
  fact: {
    flex: compact ? undefined : 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    padding: 16,
    borderTopWidth: compact ? 1 : 0,
    borderLeftWidth: compact ? 0 : 1,
    borderColor: palette.border,
  },
  factFirst: { borderTopWidth: 0, borderLeftWidth: 0 },
  factIcon: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.preview,
    marginTop: 1,
  },
  factCopy: { flex: 1, minWidth: 0, gap: 4 },
  factBody: { lineHeight: 20 },
  discover: { marginTop: 24, paddingTop: 20, borderTopWidth: 1, borderTopColor: palette.border, gap: 14, paddingBottom: 4 },
  discoverHead: { gap: 6 },
  search: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 13, minHeight: 42, borderRadius: 10, borderWidth: 1, borderColor: palette.border, backgroundColor: palette.surface },
  searchInput: { flex: 1, minWidth: 0, color: colors.text_color_primary, fontSize: 14, paddingVertical: 10, ...(isWeb ? { outlineStyle: 'none' } : {}) },
  clear: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center' },
});

export default React.memo(AgentsIntroHero);
